import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  Plus,
  Search,
  Clock,
  User,
  Mail,
  Phone,
  Check,
  CheckCircle,
  XCircle,
  AlertCircle,
  MoreVertical,
  Edit,
  Trash2,
  X,
  Settings,
  Download,
  Link,
  Copy,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  List,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import useReservations from "../../api/reservation";
import useCompany from "../../api/company";
import { useAuth } from "../../context/AuthContext";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  parseISO,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  getDay,
  addMinutes,
  setHours,
  setMinutes,
  isWithinInterval,
  addDays,
} from "date-fns";
import { nl } from "date-fns/locale";
import DateDetailModal from "./reservations/DateDetailModal";
import ReservationModal from "./reservations/ReservationModal";
import ReservationSettingsModal from "./reservations/ReservationSettingsModal";

const ReservationsManager = () => {
  const {
    getAllReservations,
    getReservationsByDateRange,
    createReservation,
    updateReservation,
    deleteReservation,
    getICSLink,
  } = useReservations();
  const { updateCompany } = useCompany();
  const { company, updateCompanyContext } = useAuth();
  const { theme } = useTheme();
  // Agendalink met geheime token: vooraf ophalen, zodat kopiëren meteen kan
  // (Safari staat het klembord niet toe na een wachttijd).
  const [icsUrl, setIcsUrl] = useState(null);
  useEffect(() => {
    if (!company?.id) return;
    getICSLink()
      .then(setIcsUrl)
      .catch((err) => console.error("Agendalink ophalen mislukt:", err));
  }, [company?.id, getICSLink]);
  const isDark = theme === "dark";

  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("calendar"); // calendar or list
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [availabilitySettings, setAvailabilitySettings] = useState({
    standard: {
      1: { enabled: true, start: "09:00", end: "17:00", slotDuration: 60 }, // Monday
      2: { enabled: true, start: "09:00", end: "17:00", slotDuration: 60 }, // Tuesday
      3: { enabled: true, start: "09:00", end: "17:00", slotDuration: 60 }, // Wednesday
      4: { enabled: true, start: "09:00", end: "17:00", slotDuration: 60 }, // Thursday
      5: { enabled: true, start: "09:00", end: "17:00", slotDuration: 60 }, // Friday
      6: { enabled: false, start: "10:00", end: "16:00", slotDuration: 60 }, // Saturday
      0: { enabled: false, start: "10:00", end: "16:00", slotDuration: 60 }, // Sunday
    },
    exceptions: {},
  });

  useEffect(() => {
    if (company?.availability_settings) {
      try {
        const settings =
          typeof company.availability_settings === "string"
            ? JSON.parse(company.availability_settings)
            : company.availability_settings;
        setAvailabilitySettings((prev) => ({ ...prev, ...settings }));
      } catch (e) {
        console.error("Error parsing availability settings", e);
      }
    }
  }, [company]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDateReservations, setSelectedDateReservations] = useState([]);
  const [settingsInitialDate, setSettingsInitialDate] = useState(null);

  useEffect(() => {
    fetchReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  useEffect(() => {
    filterReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations, statusFilter, searchQuery]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const start = format(startOfMonth(currentMonth), "yyyy-MM-dd");
      const end = format(endOfMonth(currentMonth), "yyyy-MM-dd");
      const data = await getReservationsByDateRange(start, end);
      setReservations(data);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterReservations = () => {
    let filtered = [...reservations];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.customer_name.toLowerCase().includes(query) ||
          r.customer_email.toLowerCase().includes(query) ||
          r.customer_phone?.toLowerCase().includes(query) ||
          r.service_type?.toLowerCase().includes(query),
      );
    }

    setFilteredReservations(filtered);
  };

  const getReservationsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return filteredReservations.filter(
      (r) => format(parseISO(r.reservation_date), "yyyy-MM-dd") === dateStr,
    );
  };

  const getBookedTimeSlots = (date, excludeReservationId = null) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return reservations
      .filter(
        (r) =>
          format(parseISO(r.reservation_date), "yyyy-MM-dd") === dateStr &&
          r.status !== "cancelled" &&
          r.id !== excludeReservationId,
      )
      .map((r) => ({
        start: r.start_time,
        end: r.end_time,
        customerName: r.customer_name,
      }));
  };

  const isTimeSlotAvailable = (
    date,
    startTime,
    endTime,
    excludeReservationId = null,
  ) => {
    // 1. Check against availability settings
    const dateStr = format(date, "yyyy-MM-dd");
    const dayOfWeek = getDay(date); // 0 = Sunday, 1 = Monday, etc.

    let config = availabilitySettings.standard[dayOfWeek];

    // Check for exception
    if (availabilitySettings.exceptions[dateStr]) {
      config = availabilitySettings.exceptions[dateStr];
    }

    // If no config found (shouldn't happen with defaults) or disabled
    if (!config || !config.enabled) {
      return { available: false, reason: "Gesloten op deze dag" };
    }

    // Check if time is within opening hours
    if (startTime < config.start || endTime > config.end) {
      return {
        available: false,
        reason: `Buiten openingstijden (${config.start} - ${config.end})`,
      };
    }

    // 2. Check against existing reservations
    const bookedSlots = getBookedTimeSlots(date, excludeReservationId);

    for (const slot of bookedSlots) {
      // Check if times overlap
      if (
        (startTime >= slot.start && startTime < slot.end) ||
        (endTime > slot.start && endTime <= slot.end) ||
        (startTime <= slot.start && endTime >= slot.end)
      ) {
        return { available: false, conflictWith: slot.customerName };
      }
    }
    return { available: true };
  };

  const handleAddExceptionFromCalendar = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    setSettingsInitialDate(dateStr);
    setShowDateModal(false);
    setShowSettingsModal(true);
  };

  const handleCreateReservation = () => {
    setEditingReservation(null);
    setShowModal(true);
  };

  const handleEditReservation = (reservation) => {
    setEditingReservation(reservation);
    setShowModal(true);
  };

  const handleDeleteReservation = async (id) => {
    if (
      window.confirm("Weet je zeker dat je deze reservering wilt verwijderen?")
    ) {
      try {
        await deleteReservation(id);
        await fetchReservations();
      } catch (error) {
        console.error("Error deleting reservation:", error);
        alert("Fout bij verwijderen reservering");
      }
    }
  };

  const handleStatusChange = async (reservation, newStatus) => {
    try {
      await updateReservation(reservation.id, { status: newStatus });
      await fetchReservations();

      // Update selectedDateReservations for live updates in modal
      setSelectedDateReservations((prev) =>
        prev.map((r) =>
          r.id === reservation.id ? { ...r, status: newStatus } : r,
        ),
      );
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Fout bij wijzigen status");
    }
  };

  const statusConfig = {
    pending: {
      label: "In afwachting",
      color: isDark
        ? "bg-yellow-500/15 text-yellow-400"
        : "bg-yellow-50 text-yellow-700",
      icon: AlertCircle,
    },
    confirmed: {
      label: "Bevestigd",
      color: isDark
        ? "bg-green-500/15 text-green-400"
        : "bg-green-50 text-green-700",
      icon: CheckCircle,
    },
    cancelled: {
      label: "Geannuleerd",
      color: isDark ? "bg-red-500/15 text-red-400" : "bg-red-50 text-red-700",
      icon: XCircle,
    },
    completed: {
      label: "Voltooid",
      color: isDark
        ? "bg-blue-500/15 text-blue-400"
        : "bg-blue-50 text-blue-700",
      icon: CheckCircle,
    },
  };

  // Calendar generation
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1
              className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Reserveringen
            </h1>
            <p
              className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Beheer al je afspraken en boekingen
            </p>
          </div>
          <button
            onClick={handleCreateReservation}
            className={`h-9 px-4 rounded-lg text-[13px] font-medium flex items-center justify-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            <Plus className="w-3.5 h-3.5" />
            Nieuwe Reservering
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => setShowSettingsModal(true)}
            className={`h-9 rounded-lg text-[12px] font-medium flex items-center justify-center gap-1.5 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            <Settings className="w-3.5 h-3.5" />
            Instellingen
          </button>
          <button
            onClick={() => {
              if (icsUrl) {
                navigator.clipboard.writeText(icsUrl);
                setCopiedLink(true);
                setTimeout(() => setCopiedLink(false), 2000);
              }
            }}
            className={`h-9 rounded-lg text-[12px] font-medium flex items-center justify-center gap-1.5 border transition-all duration-200 ${
              copiedLink
                ? isDark
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                  : "border-emerald-300 bg-emerald-50 text-emerald-600"
                : isDark
                  ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {copiedLink ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Gekopieerd!
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Kopieer Link
              </>
            )}
          </button>
          <button
            onClick={() => {
              if (icsUrl) {
                window.open(icsUrl, "_blank");
              }
            }}
            className={`h-9 rounded-lg text-[12px] font-medium flex items-center justify-center gap-1.5 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </button>
        </div>
      </div>

      {/* Filters */}
      <div
        className={`rounded-xl p-4 border ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
      >
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
          <div className="w-full md:flex-1 min-w-[200px] max-w-md relative">
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
            <input
              type="text"
              placeholder="Zoek op naam, email, telefoon..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full h-9 pl-9 pr-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"}`}
            />
          </div>

          <div className="flex overflow-x-auto gap-1.5 w-full md:w-auto no-scrollbar">
            {["all", "pending", "confirmed", "cancelled", "completed"].map(
              (status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 h-8 rounded-lg text-[12px] font-medium whitespace-nowrap transition-colors ${
                    statusFilter === status
                      ? isDark
                        ? "bg-white text-black"
                        : "bg-gray-900 text-white"
                      : isDark
                        ? "text-gray-500 hover:text-white hover:bg-white/[0.04]"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {status === "all" ? "Alle" : statusConfig[status]?.label}
                </button>
              ),
            )}
          </div>

          <div
            className={`flex gap-1 p-0.5 rounded-lg w-full md:w-auto ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
          >
            <button
              onClick={() => setViewMode("calendar")}
              className={`flex-1 md:flex-none h-7 px-3 rounded-md text-[12px] font-medium flex items-center justify-center gap-1.5 transition-colors ${
                viewMode === "calendar"
                  ? isDark
                    ? "bg-white text-black"
                    : "bg-white text-gray-900 shadow-sm"
                  : isDark
                    ? "text-gray-500"
                    : "text-gray-500"
              }`}
            >
              <CalendarDays className="w-3.5 h-3.5" />
              Kalender
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex-1 md:flex-none h-7 px-3 rounded-md text-[12px] font-medium flex items-center justify-center gap-1.5 transition-colors ${
                viewMode === "list"
                  ? isDark
                    ? "bg-white text-black"
                    : "bg-white text-gray-900 shadow-sm"
                  : isDark
                    ? "text-gray-500"
                    : "text-gray-500"
              }`}
            >
              <List className="w-3.5 h-3.5" />
              Lijst
            </button>
          </div>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <div
          className={`rounded-xl p-4 md:p-6 border ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
        >
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-400 hover:bg-white/[0.06] hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h2
              className={`text-[14px] font-semibold capitalize ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {format(currentMonth, "MMMM yyyy", { locale: nl })}
            </h2>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-400 hover:bg-white/[0.06] hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="w-full">
            <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
              {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((day) => (
                <div
                  key={day}
                  className={`text-center text-[11px] font-semibold uppercase tracking-wider py-2 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  {day}
                </div>
              ))}

              {calendarDays.map((day, idx) => {
                const dayReservations = getReservationsForDate(day);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isToday = isSameDay(day, new Date());

                const dateStr = format(day, "yyyy-MM-dd");
                const exception = availabilitySettings.exceptions?.[dateStr];

                return (
                  <motion.div
                    key={idx}
                    whileHover={{ scale: 1.02 }}
                    className={`min-h-[80px] sm:min-h-[96px] p-1.5 sm:p-2 border rounded-xl transition cursor-pointer ${
                      isToday
                        ? isDark
                          ? "border-white/20 bg-white/[0.04]"
                          : "border-gray-300 bg-gray-50"
                        : exception
                          ? exception.enabled
                            ? isDark
                              ? "border-blue-500/20 bg-blue-500/5"
                              : "border-blue-200 bg-blue-50/50"
                            : isDark
                              ? "border-red-500/15 bg-red-500/5"
                              : "border-red-200/60 bg-red-50/30"
                          : isCurrentMonth
                            ? isDark
                              ? "border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04]"
                              : "border-gray-100 bg-white hover:bg-gray-50/50"
                            : isDark
                              ? "border-transparent opacity-30"
                              : "border-transparent opacity-40"
                    }`}
                    onClick={() => {
                      setSelectedDate(day);
                      setSelectedDateReservations(dayReservations);
                      setShowDateModal(true);
                    }}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div
                        className={`text-[12px] font-medium ${
                          isToday
                            ? isDark
                              ? "text-white"
                              : "text-gray-900"
                            : isCurrentMonth
                              ? isDark
                                ? "text-gray-300"
                                : "text-gray-700"
                              : isDark
                                ? "text-gray-600"
                                : "text-gray-400"
                        }`}
                      >
                        {format(day, "d")}
                      </div>
                      {exception && (
                        <div
                          className={`text-[9px] ${exception.enabled ? (isDark ? "text-blue-400" : "text-blue-500") : ""}`}
                        >
                          {exception.enabled ? (
                            <Settings className="w-3 h-3" />
                          ) : (
                            <span
                              className={`px-1 py-0.5 rounded text-[9px] font-medium ${isDark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-600"}`}
                            >
                              Dicht
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {dayReservations.length > 0 && (
                      <div className="space-y-0.5">
                        {dayReservations.slice(0, 3).map((reservation) => {
                          const StatusIcon =
                            statusConfig[reservation.status]?.icon;
                          return (
                            <div
                              key={reservation.id}
                              className={`text-[10px] sm:text-[11px] px-1 py-0.5 rounded-md truncate flex items-center gap-0.5 ${statusConfig[reservation.status]?.color}`}
                            >
                              <StatusIcon className="w-2.5 h-2.5 flex-shrink-0" />
                              <span className="truncate hidden sm:inline">
                                {reservation.start_time.slice(0, 5)}{" "}
                                {reservation.customer_name}
                              </span>
                              <span className="truncate sm:hidden">
                                {reservation.start_time.slice(0, 5)}
                              </span>
                            </div>
                          );
                        })}
                        {dayReservations.length > 3 && (
                          <div
                            className={`text-[10px] text-center font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          >
                            +{dayReservations.length - 3}
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <div
          className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
        >
          {loading ? (
            <div
              className={`p-8 text-center text-[13px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Laden...
            </div>
          ) : filteredReservations.length === 0 ? (
            <div className="text-center py-12">
              <div
                className={`h-12 w-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-50"}`}
              >
                <CalendarDays
                  className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                />
              </div>
              <p
                className={`text-[13px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Geen reserveringen gevonden
              </p>
              <p
                className={`text-[12px] mt-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              >
                Pas je filters aan of maak een nieuwe reservering aan
              </p>
            </div>
          ) : (
            <div
              className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-50"}`}
            >
              {filteredReservations.map((reservation, i) => {
                const StatusIcon = statusConfig[reservation.status]?.icon;
                return (
                  <motion.div
                    key={reservation.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className={`p-4 transition-colors ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50/50"}`}
                  >
                    <div className="flex flex-col md:flex-row items-start justify-between gap-3">
                      <div className="flex-1 space-y-2 w-full">
                        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                          <div className="flex items-center gap-2">
                            <div
                              className={`h-6 w-6 rounded-full flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                            >
                              <User
                                className={`w-3 h-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              />
                            </div>
                            <span
                              className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {reservation.customer_name}
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-1.5 text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            <Mail className="w-3 h-3" />
                            {reservation.customer_email}
                          </div>
                          {reservation.customer_phone && (
                            <div
                              className={`flex items-center gap-1.5 text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                            >
                              <Phone className="w-3 h-3" />
                              {reservation.customer_phone}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 md:gap-3">
                          <span
                            className={`flex items-center gap-1.5 text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            <Calendar className="w-3 h-3" />
                            {format(
                              parseISO(reservation.reservation_date),
                              "dd/MM/yyyy",
                              { locale: nl },
                            )}
                          </span>
                          <span
                            className={`flex items-center gap-1.5 text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            <Clock className="w-3 h-3" />
                            {reservation.start_time.slice(0, 5)} –{" "}
                            {reservation.end_time.slice(0, 5)}
                          </span>
                          {reservation.service_type && (
                            <span
                              className={`text-[11px] font-medium px-2 py-0.5 rounded-md ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-100 text-gray-600"}`}
                            >
                              {reservation.service_type}
                            </span>
                          )}
                        </div>

                        {reservation.notes && (
                          <p
                            className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            {reservation.notes}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 w-full md:w-auto justify-between md:justify-end">
                        <div className="flex items-center gap-2">
                          <select
                            value={reservation.status}
                            onChange={(e) =>
                              handleStatusChange(reservation, e.target.value)
                            }
                            className={`h-7 px-2 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                          >
                            <option value="pending">In afwachting</option>
                            <option value="confirmed">Bevestigd</option>
                            <option value="cancelled">Geannuleerd</option>
                            <option value="completed">Voltooid</option>
                          </select>

                          <span
                            className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ${statusConfig[reservation.status]?.color}`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            <span className="hidden sm:inline">
                              {statusConfig[reservation.status]?.label}
                            </span>
                          </span>
                        </div>

                        <div className="relative group">
                          <button
                            className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                          >
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                          <div
                            className={`hidden group-hover:block absolute right-0 mt-1 w-40 rounded-xl border z-10 py-1 ${isDark ? "bg-[#1a1a1a] border-white/[0.08]" : "bg-white border-gray-200 shadow-lg"}`}
                          >
                            <button
                              onClick={() => handleEditReservation(reservation)}
                              className={`w-full px-3 py-2 text-left text-[12px] flex items-center gap-2 ${isDark ? "text-gray-300 hover:bg-white/[0.04]" : "text-gray-700 hover:bg-gray-50"}`}
                            >
                              <Edit className="w-3.5 h-3.5" />
                              Bewerken
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteReservation(reservation.id)
                              }
                              className={`w-full px-3 py-2 text-left text-[12px] flex items-center gap-2 ${isDark ? "text-red-400 hover:bg-red-500/10" : "text-red-600 hover:bg-red-50"}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Verwijderen
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Reservation Modal */}
      {showModal && (
        <ReservationModal
          reservation={editingReservation}
          onClose={() => setShowModal(false)}
          onSave={async () => {
            await fetchReservations();
            setShowModal(false);
          }}
          getBookedTimeSlots={getBookedTimeSlots}
          isTimeSlotAvailable={isTimeSlotAvailable}
          settings={availabilitySettings}
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <ReservationSettingsModal
          open={showSettingsModal}
          onOpenChange={(open) => {
            setShowSettingsModal(open);
            if (!open) setSettingsInitialDate(null);
          }}
          settings={availabilitySettings}
          initialDate={settingsInitialDate}
          onSave={async (newSettings) => {
            try {
              setAvailabilitySettings(newSettings);
              await updateCompany(company.id, {
                availability_settings: newSettings,
              });
              if (updateCompanyContext) {
                updateCompanyContext({ availability_settings: newSettings });
              }
              setShowSettingsModal(false);
            } catch (error) {
              console.error("Failed to save settings:", error);
              alert("Fout bij opslaan van instellingen");
            }
          }}
        />
      )}

      {/* Date Detail Modal */}
      {showDateModal && (
        <DateDetailModal
          date={selectedDate}
          reservations={selectedDateReservations}
          onClose={() => setShowDateModal(false)}
          onAddException={() => handleAddExceptionFromCalendar(selectedDate)}
          onEdit={(reservation) => {
            setEditingReservation(reservation);
            setShowDateModal(false);
            setShowModal(true);
          }}
          onStatusChange={handleStatusChange}
          onDelete={handleDeleteReservation}
          statusConfig={statusConfig}
        />
      )}
    </div>
  );
};

export default ReservationsManager;
